import { YARD_STATE_LABELS } from "@etudescarre/constants";
import { Button, cn, Modal } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { useState } from "react";
import { useParams } from "react-router";
import { toaster } from "@/app";
import { getYardState, isYardToggleOn } from "@/features/staff/yard-state";
import { apiClient } from "@/lib/api/client";
import { useYardContext } from "@/providers/yard-context";

export function YardCommissionToggle() {
  const { yard } = useYardContext();
  const { yardId } = useParams<{ yardId: string }>();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const isCommissioned = isNotNil(yard.comissioningDate);
  const isOn = isYardToggleOn(yard);
  const canCommission =
    isNotNil(yard.expirationDate) &&
    isNotNil(yard.beforeArchivingDays) &&
    !isCommissioned;

  const handleConfirm = async () => {
    if (isNil(yardId)) return;

    setLoading(true);
    const { error } = await apiClient.PATCH("/api/yards/{yardId}/commission", {
      params: { path: { yardId } },
    });
    setLoading(false);

    if (isNotNil(error)) {
      toaster.error({
        title: "Erreur",
        description: "Impossible de mettre en service le chantier.",
      });
      setOpen(false);
      return;
    }

    toaster.success({
      title: "Mise en service",
      description: "Le chantier a été mis en service avec succès.",
    });
    setOpen(false);
    // TODO: replace with context refetch when YardProvider exposes one
    window.location.reload();
  };

  const getStatusLabel = () => {
    if (isCommissioned) return YARD_STATE_LABELS[getYardState(yard)];
    if (!canCommission) return "Non configurable";
    return "Hors service";
  };

  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        role="switch"
        aria-checked={isOn}
        disabled={!canCommission}
        onClick={() => setOpen(true)}
        className={cn(
          "relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          isOn ? "bg-emerald-500" : "bg-zinc-300 dark:bg-zinc-600",
        )}
      >
        <span
          className={cn(
            "pointer-events-none block size-4 rounded-full bg-white shadow-sm ring-0 transition-transform",
            isOn ? "translate-x-6" : "translate-x-1",
          )}
        />
      </button>
      <span className="text-sm text-zinc-600 dark:text-zinc-400">
        {getStatusLabel()}
      </span>

      <Modal.Root open={open} onOpenChange={(details) => setOpen(details.open)}>
        <Modal.Backdrop />
        <Modal.Positioner>
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>Mise en service du chantier</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>
                Vous êtes sur le point de mettre en service le chantier et d'en
                informer les utilisateurs. Assurez-vous d'avoir bien terminé son
                paramétrage initial avant de valider.
              </p>
            </Modal.Body>
            <Modal.Footer>
              <Button outline onClick={() => setOpen(false)} disabled={loading}>
                Annuler
              </Button>
              <Button onClick={handleConfirm} disabled={loading}>
                {loading ? "En cours..." : "Mettre en service"}
              </Button>
            </Modal.Footer>
          </Modal.Content>
        </Modal.Positioner>
      </Modal.Root>
    </div>
  );
}
